/**
 * Phase 2b — Recovery + Strain scoring config.
 *
 * ALL numeric thresholds/weights/ceilings live here as DEFAULTS. Tune later
 * against real data; never sprinkle these numbers through the codebase.
 */

// ─── Baseline / calibration ────────────────────────────────────────────────
export const BASELINE_WINDOW_DAYS = 14;
export const CALIBRATION_MIN_DAYS = 2; // < this → 'insufficient'
export const CALIBRATION_READY_DAYS = 5; // ≥ this → 'ready'; between → 'calibrating'

// ─── Recovery ──────────────────────────────────────────────────────────────
export const RECOVERY_WEIGHTS = {
  sleep: 0.4,
  hrv: 0.35,
  rhr: 0.25,
} as const;

export const RECOVERY = {
  /** Below this many minutes of sleep, sub-score is capped regardless of baseline. */
  SLEEP_ABSOLUTE_FLOOR_MIN: 300, // 5h
  SLEEP_ABSOLUTE_FLOOR_CAP: 30,

  /** Sleep piecewise: today/baseline ratio. */
  SLEEP_FULL_CREDIT_RATIO: 1.0,
  SLEEP_ZERO_CREDIT_RATIO: 0.6,

  /** HRV piecewise: today/baseline ratio. Higher = better. */
  HRV_FULL_CREDIT_RATIO: 1.0,
  HRV_ZERO_CREDIT_RATIO: 0.6,

  /** RHR piecewise: today/baseline ratio. Lower = better. */
  RHR_FULL_CREDIT_RATIO: 1.0,
  RHR_ZERO_CREDIT_RATIO: 1.15,
} as const;

export const RECOVERY_BANDS = {
  LOW_MAX: 33, // ≤33 → low
  MODERATE_MAX: 66, // 34-66 → moderate; ≥67 → high
} as const;

// ─── Strain ────────────────────────────────────────────────────────────────
export const STRAIN_WEIGHTS = {
  lifting: 0.5,
  cardio: 0.35,
  steps: 0.15,
} as const;

/**
 * Cold-start ceilings — used for every user today.
 *
 * TODO(strain-personalised): once a user has ≥14 days of strain history,
 * shift these toward their personal high (e.g. 90th-percentile volume /
 * kcal / steps over the window). `computeStrain` already takes ceilings
 * as a parameter — the orchestrator swap is one line.
 */
export const STRAIN_CEILINGS_DEFAULT = {
  LIFTING_HARD_VOLUME_KG: 12000,
  CARDIO_BIG_DAY_KCAL: 700,
  STEPS_BIG_DAY: 15000,
} as const;

export const STRAIN_PERSONALISED = false as const;

export const STRAIN_BANDS = {
  LIGHT_MAX: 33,
  MODERATE_MAX: 66,
} as const;

export type StrainCeilings = typeof STRAIN_CEILINGS_DEFAULT;
